import React from 'react';
import { Building2, Home, Clock, Calculator } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface TransactionTypeSelectorProps {
  selectedType: string;
  onSelect: (type: string) => void;
}

const transactionTypes = [
  {
    id: 'estimation',
    icon: Calculator,
    label: 'Estimation',
    description: 'Estimer la valeur d’un bien',
    color: 'purple',
  },
  {
    id: 'sale',
    icon: Building2,
    label: 'Vente',
    description: 'Ajouter un bien à vendre',
    color: 'blue',
  },
  {
    id: 'rent',
    icon: Home,
    label: 'Location',
    description: 'Ajouter un bien à louer',
    color: 'green',
  },
  {
    id: 'short-rent',
    icon: Clock,
    label: 'Location courte durée',
    description: 'Location saisonnière',
    color: 'orange',
  },
];

export const TransactionTypeSelector: React.FC<TransactionTypeSelectorProps> = ({
  selectedType,
  onSelect,
}) => {
  return (
    <div className="grid gap-4 grid-cols-2">
      {transactionTypes.map((type) => {
        const Icon = type.icon;
        const isSelected = selectedType === type.id && selectedType !== '';
        const isPurple = type.color === 'purple';
        const isBlue = type.color === 'blue';
        const isGreen = type.color === 'green';
        const isOrange = type.color === 'orange';

        return (
          <button
            key={type.id}
            onClick={() => onSelect(type.id)}
            className={cn(
              'group flex flex-col items-center gap-3 rounded-xl p-6 transition-all duration-200 ease-in-out transform hover:scale-105',
              'border-2 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md',
              isSelected
                ? isPurple
                  ? 'border-purple-500 dark:border-purple-400'
                  : isBlue
                  ? 'border-blue-500 dark:border-blue-400'
                  : isGreen
                  ? 'border-green-500 dark:border-green-400'
                  : 'border-orange-500 dark:border-orange-400'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            )}
            style={{
              '--hover-shadow': isSelected 
                ? 'none'
                : isPurple 
                ? '0 10px 25px -5px rgba(147, 51, 234, 0.3)'
                : isBlue
                ? '0 10px 25px -5px rgba(59, 130, 246, 0.3)'
                : isGreen
                ? '0 10px 25px -5px rgba(34, 197, 94, 0.3)'
                : '0 10px 25px -5px rgba(249, 115, 22, 0.3)'
            } as React.CSSProperties}
            onMouseEnter={(e) => {
              if (!isSelected) {
                e.currentTarget.style.boxShadow = e.currentTarget.style.getPropertyValue('--hover-shadow');
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                e.currentTarget.style.boxShadow = '';
              }
            }}
          >
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-lg p-2 transition-all duration-200 group-hover:scale-110',
                isSelected
                  ? isPurple
                    ? 'bg-purple-500 text-white dark:bg-purple-600'
                    : isBlue
                    ? 'bg-blue-500 text-white dark:bg-blue-600'
                    : isGreen
                    ? 'bg-green-500 text-white dark:bg-green-600'
                    : 'bg-orange-500 text-white dark:bg-orange-600'
                  : isPurple
                  ? 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 group-hover:bg-purple-100 group-hover:text-purple-600 dark:group-hover:bg-purple-900/30 dark:group-hover:text-purple-400'
                  : isBlue
                  ? 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 group-hover:bg-blue-100 group-hover:text-blue-600 dark:group-hover:bg-blue-900/30 dark:group-hover:text-blue-400'
                  : isGreen
                  ? 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 group-hover:bg-green-100 group-hover:text-green-600 dark:group-hover:bg-green-900/30 dark:group-hover:text-green-400'
                  : 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 group-hover:bg-orange-100 group-hover:text-orange-600 dark:group-hover:bg-orange-900/30 dark:group-hover:text-orange-400'
              )}
            >
              <Icon className="h-6 w-6" />
            </div>
            <div className="text-center">
              <h3
                className={cn(
                  'text-lg font-semibold transition-colors duration-200',
                  isSelected
                    ? isPurple
                      ? 'text-purple-700 dark:text-purple-300'
                      : isBlue
                      ? 'text-blue-700 dark:text-blue-300'
                      : isGreen
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-orange-700 dark:text-orange-300'
                    : isPurple
                    ? 'text-gray-900 dark:text-white group-hover:text-purple-700 dark:group-hover:text-purple-300'
                    : isBlue
                    ? 'text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300'
                    : isGreen
                    ? 'text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-300'
                    : 'text-gray-900 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-300'
                )}
              >
                {type.label}
              </h3>
              <p
                className={cn(
                  'mt-1 text-sm transition-colors duration-200',
                  isSelected
                    ? isPurple
                      ? 'text-purple-600 dark:text-purple-400'
                      : isBlue
                      ? 'text-blue-600 dark:text-blue-400'
                      : isGreen
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-orange-600 dark:text-orange-400'
                    : isPurple
                    ? 'text-gray-500 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400'
                    : isBlue
                    ? 'text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                    : isGreen
                    ? 'text-gray-500 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400'
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-orange-600 dark:group-hover:text-orange-400'
                )}
              >
                {type.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};
